import { Collapse, Form, Segmented, Select, Slider, Typography } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { datasetByKey } from "@/warRoom/datasets/registry";
import type { Field } from "@/warRoom/datasets/types";
import { AGG_LABEL } from "@/warRoom/graph/aggregate";
import {
  aggregationsFor,
  formByType,
  seriesFieldsFor,
  xCandidatesFor,
  xFieldsFor,
  yCandidatesFor,
} from "@/warRoom/graph/chartForms";
import type { Aggregation, SortBy, TimeGrain } from "@/warRoom/graph/spec";
import {
  useGraphStore,
  useSelectedWidget,
} from "@/warRoom/store/useGraphStore";

const { Text } = Typography;

/** antd 的 Select 不接受 null 當 option value，用哨符字串代表「不選」 */
const NONE = "__none__";

const GRAIN_OPTIONS: TimeGrain[] = ["day", "week", "month", "quarter", "year"];

const SORT_OPTIONS: SortBy[] = ["valueDesc", "valueAsc", "x"];

/** 值多到一列一根柱子的欄位照樣能選，但要先說清楚 */
const opt = (fields: Field[], t: TFunction) =>
  fields.map((f) => ({
    value: f.key,
    label: f.highCardinality
      ? t("warRoom.axis.highCardinality", { field: f.label })
      : f.label,
  }));

/**
 * X / Y 軸（作用在畫布上選中的那張圖）。
 * 兩個下拉都列出這份資料的「全部欄位」，只是分成「適合這種圖」和「其他欄位」兩組 ——
 * 建議歸建議，要選什麼是使用者決定的。少用的排序與前 N 收在「更多設定」裡。
 */
export default function AxisPanel() {
  const { t } = useTranslation();
  const widget = useSelectedWidget();
  const {
    setXField,
    setYField,
    setAggregation,
    setSeriesField,
    setTimeGrain,
    setTopN,
    setSortBy,
  } = useGraphStore();

  const spec = widget?.spec;
  const dataset = spec?.datasetKey ? datasetByKey(spec.datasetKey) : undefined;
  if (!spec || !dataset || !spec.chartType) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t("warRoom.axis.empty")}
      </Text>
    );
  }

  const form = formByType(spec.chartType);
  const isScatter = spec.chartType === "scatter";

  /* --------------------------------- X 軸 --------------------------------- */
  const xAll = xCandidatesFor(dataset, form);
  const xSuggested = isScatter ? xAll : xFieldsFor(dataset, form);
  const suggestedKeys = new Set(xSuggested.map((f) => f.key));
  const xRest = xAll.filter((f) => !suggestedKeys.has(f.key));
  const xOptions: DefaultOptionType[] =
    xRest.length === 0 || xSuggested.length === 0
      ? opt(xAll, t)
      : [
          { label: t("warRoom.axis.suggested"), options: opt(xSuggested, t) },
          { label: t("warRoom.axis.otherFields"), options: opt(xRest, t) },
        ];

  const xMeta = dataset.fields.find((f) => f.key === spec.xField);
  const xIsTime = xMeta?.role === "time";
  const xIsSuggested = !spec.xField || suggestedKeys.has(spec.xField);

  /* --------------------------------- Y 軸 --------------------------------- */
  const yAll = yCandidatesFor(dataset, form, spec.xField);
  const yMeasures = yAll.filter((f) => f.role === "measure");
  const yOthers = yAll.filter((f) => f.role !== "measure");
  const yOptions: DefaultOptionType[] = isScatter
    ? opt(yAll, t)
    : [
        {
          label: t("warRoom.axis.noMeasure"),
          options: [{ value: NONE, label: t("warRoom.axis.countOption") }],
        },
        ...(yMeasures.length > 0
          ? [{ label: t("warRoom.axis.measures"), options: opt(yMeasures, t) }]
          : []),
        ...(yOthers.length > 0
          ? [
              {
                label: t("warRoom.axis.othersDistinct"),
                options: opt(yOthers, t),
              },
            ]
          : []),
      ];

  const yMeta = spec.yField
    ? dataset.fields.find((f) => f.key === spec.yField)
    : undefined;
  const aggs = aggregationsFor(yMeta);

  const seriesOptions = seriesFieldsFor(dataset, spec.xField);
  const showMore = !xIsTime && !isScatter;

  return (
    <Form layout="vertical" size="small" style={{ marginBottom: 0 }}>
      <Form.Item
        label={isScatter ? t("warRoom.axis.xMeasure") : t("warRoom.axis.x")}
        style={{ marginBottom: 10 }}
      >
        <Select
          value={spec.xField ?? undefined}
          onChange={setXField}
          options={xOptions}
          placeholder={t("warRoom.axis.xPlaceholder")}
          showSearch
          optionFilterProp="label"
        />
        {xIsTime ? (
          <Segmented
            size="small"
            block
            style={{ marginTop: 6 }}
            value={spec.timeGrain}
            onChange={(v) => setTimeGrain(v as TimeGrain)}
            options={GRAIN_OPTIONS.map((g) => ({
              value: g,
              label: t(`warRoom.axis.grain.${g}`),
            }))}
          />
        ) : null}
        {!xIsSuggested ? (
          <Text type="secondary" className="field-hint">
            {t(
              xIsTime
                ? "warRoom.axis.notSuggestedTime"
                : "warRoom.axis.notSuggestedDim",
              { field: xMeta?.label, chart: form.label },
            )}
          </Text>
        ) : null}
      </Form.Item>

      <Form.Item
        label={isScatter ? t("warRoom.axis.yMeasure") : t("warRoom.axis.y")}
        style={{ marginBottom: 10 }}
      >
        <Select
          value={spec.yField ?? (isScatter ? undefined : NONE)}
          onChange={(v) => setYField(v === NONE ? null : v)}
          options={yOptions}
          placeholder={t("warRoom.axis.yPlaceholder")}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>

      {!isScatter && spec.yField ? (
        <Form.Item
          label={t("warRoom.axis.aggregation")}
          style={{ marginBottom: 10 }}
        >
          {aggs.length > 1 ? (
            <Segmented
              size="small"
              block
              value={spec.aggregation}
              onChange={(v) => setAggregation(v as Aggregation)}
              options={aggs.map((a) => ({ value: a, label: AGG_LABEL[a] }))}
            />
          ) : (
            <Text
              type="secondary"
              className="field-hint"
              style={{ marginTop: 0 }}
            >
              {t("warRoom.axis.distinctOnly", {
                field: yMeta?.label,
                agg: AGG_LABEL[aggs[0]],
              })}
            </Text>
          )}
        </Form.Item>
      ) : null}

      {form.supportsSeries ? (
        <Form.Item
          label={t(
            form.requiresSeries
              ? "warRoom.axis.seriesRequired"
              : "warRoom.axis.seriesOptional",
          )}
          style={{ marginBottom: 10 }}
        >
          <Select
            value={spec.seriesField ?? NONE}
            onChange={(v) => setSeriesField(v === NONE ? null : v)}
            options={[
              ...(form.requiresSeries
                ? []
                : [{ value: NONE, label: t("warRoom.axis.noSeries") }]),
              ...seriesOptions.map((f) => ({ value: f.key, label: f.label })),
            ]}
            placeholder={t("warRoom.axis.seriesPlaceholder")}
            showSearch
            optionFilterProp="label"
          />
          <Text type="secondary" className="field-hint">
            {t("warRoom.axis.seriesHint")}
          </Text>
        </Form.Item>
      ) : null}

      {isScatter ? (
        <Text type="secondary" className="field-hint">
          {t("warRoom.axis.scatterHint")}
        </Text>
      ) : null}

      {showMore ? (
        <Collapse
          ghost
          size="small"
          className="more-settings"
          items={[
            {
              key: "more",
              label: (
                <span style={{ fontSize: 12 }}>{t("warRoom.axis.more")}</span>
              ),
              children: (
                <>
                  <Form.Item
                    label={t("warRoom.axis.sort")}
                    style={{ marginBottom: 10 }}
                  >
                    <Select
                      value={spec.sortBy}
                      onChange={setSortBy}
                      options={SORT_OPTIONS.map((o) => ({
                        value: o,
                        label: t(`warRoom.axis.sortBy.${o}`),
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label={
                      spec.topN === 0
                        ? t("warRoom.axis.topNAll")
                        : t("warRoom.axis.topN", { n: spec.topN })
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <Slider
                      min={0}
                      max={30}
                      step={1}
                      value={spec.topN}
                      onChange={setTopN}
                    />
                    <Text type="secondary" className="field-hint">
                      {t("warRoom.axis.topNHint")}
                    </Text>
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
      ) : null}
    </Form>
  );
}
